import { ElementRef, forwardRef, memo, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import useResizeObserver from '@react-hook/resize-observer';
import { debounce } from 'lodash-es';
import { theme } from '@frontend/theme';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { useZoomStore } from '../../store/zoom.store';
import { CalendarColumnBodyVariant, CalendarEvent, CalendarGridDragStyles, EventDropColumnStyleFn } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { ColumnEvents } from '../column-events';
import { EventCardSwitch } from '../column-events/event-card-switch';

const useCalendarColumnResizeObserver = (shouldRegister: boolean) => {
  const firstColumnRef = useRef<ElementRef<'div'>>(null);
  const { setGroupColumnWidth } = useCalendarGridStore(['setGroupColumnWidth']);

  useResizeObserver(
    shouldRegister ? firstColumnRef : null,
    debounce((entry) => {
      const measuredWidth = Math.round(entry.contentRect.width);

      if (!measuredWidth || Number.isNaN(measuredWidth)) {
        return;
      }

      setGroupColumnWidth(measuredWidth);
    }, 100)
  );

  return firstColumnRef;
};

interface Props {
  isFirstGroup: boolean;
  columnIds: string[];
  groupId: string;
  startColumn: number;
  columnBodyVariant: CalendarColumnBodyVariant;
}

export const GroupBody = ({ isFirstGroup, columnIds, groupId, startColumn, columnBodyVariant }: Props) => {
  const shouldRegisterResizeObserver = isFirstGroup && columnBodyVariant === 'grid';
  const firstColumnRef = useCalendarColumnResizeObserver(shouldRegisterResizeObserver);
  const enableDroppableColumns = useCalendarGridStore((s) => s.enableDroppableColumns);

  return columnIds.map((columnId, index) => (
    <ColumnBody
      ref={index === 0 ? firstColumnRef : undefined}
      key={columnId}
      columnId={columnId}
      groupId={groupId}
      index={index}
      startColumn={startColumn}
      columnBodyVariant={columnBodyVariant}
      enableDroppableColumns={enableDroppableColumns}
    />
  ));
};

interface ColumnBodyProps {
  columnId: string;
  groupId: string;
  index: number;
  startColumn: number;
  columnBodyVariant: CalendarColumnBodyVariant;
  enableDroppableColumns: boolean;
}

const ColumnBody = forwardRef<HTMLDivElement, ColumnBodyProps>(
  ({ columnId, groupId, index, startColumn, columnBodyVariant, enableDroppableColumns }: ColumnBodyProps, ref) => {
    const dragStyles = useCalendarGridStore((s) => s.dragStyles);
    const eventDragPreview = useCalendarGridStore((s) => s.eventDragPreview);
    const eventsById = useCalendarGridStore((s) => s.eventsById);
    const pxPerMinute = useZoomStore((s) => s.pxPerMinute);
    const { setNodeRef, isOver } = useDroppable({
      id: columnId,
      data: { columnId, groupId },
      disabled: !enableDroppableColumns,
    });

    const combinedRef = (node: HTMLDivElement) => {
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }
    };

    return (
      <div
        ref={combinedRef}
        data-column-id={columnId}
        data-group-id={groupId}
        data-drop-over={isOver ? 'true' : undefined}
        css={[
          columnStyle,
          columnBodyVariant === 'stacked' && stackedColumnStyle,
          isOver &&
            (typeof dragStyles.eventDropColumn === 'function'
              ? (() => {
                  const ev = eventDragPreview?.eventId ? eventsById[eventDragPreview.eventId] : undefined;
                  return ev ? (dragStyles.eventDropColumn as EventDropColumnStyleFn)(ev, columnId, groupId) : undefined;
                })()
              : dragStyles.eventDropColumn),
        ]}
        style={{
          gridColumn: `${startColumn + index} / span 1`,
          borderInlineStartWidth: index === 0 ? 1 : 0,
        }}
      >
        <ColumnEvents columnId={columnId} />
        <CalendarDragStateContext.Provider value={{ ...defaultCalendarDragState, isEventDropColumn: isOver }}>
          <EventDropPlaceholder
            isOver={isOver}
            columnId={columnId}
            eventDragPreview={eventDragPreview}
            eventsById={eventsById}
            dragStyles={dragStyles}
            pxPerMinute={pxPerMinute}
          />
        </CalendarDragStateContext.Provider>
      </div>
    );
  }
);

ColumnBody.displayName = 'ColumnBody';

interface EventDropPlaceholderProps {
  isOver: boolean;
  columnId: string;
  eventDragPreview: { columnId: string; startMinutes: number; eventId: string } | null;
  eventsById: Record<string, CalendarEvent | undefined>;
  dragStyles: CalendarGridDragStyles;
  pxPerMinute: number;
}

const EventDropPlaceholderComponent = ({
  isOver,
  columnId,
  eventDragPreview,
  eventsById,
  dragStyles,
  pxPerMinute,
}: EventDropPlaceholderProps) => {
  if (!isOver || eventDragPreview?.columnId !== columnId || !dragStyles.eventDropOverlay) return null;

  const draggedEvent = eventsById[eventDragPreview.eventId];
  const overlayStyle =
    typeof dragStyles.eventDropOverlay === 'function'
      ? draggedEvent
        ? (dragStyles.eventDropOverlay as (e: CalendarEvent) => Interpolation<Theme>)(draggedEvent)
        : undefined
      : dragStyles.eventDropOverlay;

  if (!overlayStyle) return null;

  const styles: Interpolation<Theme>[] = [
    {
      position: 'absolute',
      pointerEvents: 'none',
      top: eventDragPreview.startMinutes * pxPerMinute,
      height: (draggedEvent?.duration ?? 30) * pxPerMinute,
    },
    overlayStyle,
  ];

  return draggedEvent ? (
    <CalendarDragStateContext.Provider
      value={{ ...defaultCalendarDragState, isEventDropOverlay: true, eventDropOverlayStyle: styles }}
    >
      <EventCardSwitch event={draggedEvent} />
    </CalendarDragStateContext.Provider>
  ) : null;
};

const EventDropPlaceholder = memo(EventDropPlaceholderComponent);

const columnStyle = css({
  gridRow: '2 / -1', // Spans all rows except the header
  display: 'grid',
  position: 'relative',
  gridTemplateRows: 'repeat(var(--total-mins), var(--row-height))', // NOTE: contentVisibility not works with subgrid
  // Native browser virtualization hint
  contentVisibility: 'auto',
  containIntrinsicSize: `var(--column-min-width) calc(var(--row-height) * var(--total-mins))`,
  // Optimization: Tell the browser this won't change often
  // contain: "layout style paint",

  borderInlineWidth: '0 1px',
  borderInlineColor: theme.colors.neutral20,
  borderInlineStyle: 'solid',
  scrollSnapAlign: 'start',
});

const stackedColumnStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(3),
  borderInline: 'none',
  padding: theme.spacing(3, 1),
});
